import { MinusButton } from "@/components/Buttons/MinusButton";
import { PlusButton } from "@/components/Buttons/PlusButton";
import { ThumbaGiftPointIcon } from "@roman/shared/components/Icons";
import { SYSTEM_FEE_MULTIPLIER } from "@roman/shared/constants";
import { yenToMiraiMile } from "@roman/shared/utils/pointsToMiraiMile";
import { pointsToYenForCalculations } from "@roman/shared/utils/pointsToYen";
import type React from "react";
import { useMemo, useState } from "react";

interface PriceSelectionCardProps {
  yen: string;
  balancePoints: number;
  onYenChange: (yen: number, quantity: number) => void;
  initialQuantity?: number;
}

export const PriceSelectionCard: React.FC<PriceSelectionCardProps> = ({
  yen,
  balancePoints,
  onYenChange,
  initialQuantity = 0,
}) => {
  const [isSelected, setIsSelected] = useState(initialQuantity > 0);
  const [quantity, setQuantity] = useState<number>(initialQuantity);

  const calculateAmountWithSystemFee = (amount: number) => amount * SYSTEM_FEE_MULTIPLIER;
  const yenWithSystemFee = calculateAmountWithSystemFee(Number(yen));

  const canAddMore = useMemo(
    () => yenWithSystemFee <= pointsToYenForCalculations(balancePoints),
    [balancePoints, yenWithSystemFee],
  );

  const handleClick = () => {
    if (!isSelected && canAddMore) {
      setIsSelected(true);
      setQuantity(1);
      onYenChange(Number(yen), 1);
    }
  };

  const handleMinusClick = () => {
    if (quantity > 0) {
      const newQuantity = quantity - 1;
      setQuantity(newQuantity);
      if (newQuantity === 0) {
        setIsSelected(false);
      }
      onYenChange(Number(yen), newQuantity);
    }
  };

  const handlePlusClick = () => {
    if (!canAddMore) return;
    const newQuantity = quantity + 1;
    setQuantity(newQuantity);
    onYenChange(Number(yen), newQuantity);
  };

  return (
    <div className="flex flex-col gap-3">
      <div
        className={`col-span-1 border border-solid ${isSelected ? "border-primary bg-[#EFF9FF]" : "border-disabled bg-white"} py-3 px-5 rounded-md cursor-pointer`}
        onClick={handleClick}
      >
        <div className="flex flex-col items-start">
          <div className={`text-[22px] font-bold ${isSelected ? "text-black" : "text-gray"}`}>
            {Number(yen).toLocaleString()}円分
          </div>
          <div className={`flex items-center gap-1 text-[12px] font-bold ${isSelected ? "text-black" : "text-gray"}`}>
            <ThumbaGiftPointIcon />
            {(Number(yenToMiraiMile(Number(yen))) * SYSTEM_FEE_MULTIPLIER).toLocaleString()}マイル
          </div>
        </div>
      </div>
      {isSelected && (
        <div className="col-span-1 flex items-center justify-center gap-[9px]">
          <p className="text-xs font-medium">交換件数</p>
          <MinusButton onClick={handleMinusClick} />
          <p className="text-base font-medium">{quantity}</p>
          {canAddMore && <PlusButton onClick={handlePlusClick} />}
        </div>
      )}
    </div>
  );
};
